<script setup lang="ts">
import Button from '~~/components/design-system/button.vue';
import InputMask from 'primevue/inputmask';
import { useAuthStore } from '~/stores/auth.store';
import GenericError from '~/components/user-interface/generic-error.vue';
import { useSiteStore } from '~/stores/site.store';
import { globalKeys } from 'assets/data/global-keys';
import { CheckCircleIcon } from '@heroicons/vue/24/outline';
import type { IFormBuilderReference } from '~/interfaces/form/formBuilder';

const formLoading = ref<boolean>(false);
const { $authService, $storageService } = useServices();
const { $trackEvent } = useNuxtApp();
const site = useSiteStore();
const authStore = useAuthStore();

const token = ref(null);
const tokenFormatted = ref(null);

const otpValid = ref<boolean>(false);
const form = ref<IFormBuilderReference>(null);
const errorCode = ref<string>('');
const errorRef = ref<string>('');

const model = ref({
  password: '',
  passwordConfirmation: '',
});

const navigateBack = () => {
  site.activateModal('passwordResetStepOne');
};

const navigateToSuccess = () => {
  site.activateModal('passwordResetStepSuccess');
};

const verifyOTP = () => {
  let otp = token.value;
  otp = otp.replace(/ +/g, '');
  if (!otp.includes('_') && otp.length > 4) {
    otpValid.value = true;
    tokenFormatted.value = otp;
  } else {
    otpValid.value = false;
  }
};

const confirmOtp = async () => {
  errorCode.value = '';
  formLoading.value = true;
  const token = tokenFormatted.value;
  const password = model.value['password'];

  try {
    const data = await $authService.passwordResetConfirm({
      password,
      login: authStore.passwordResetMobileNumber,
      token,
    });

    if (data?.isSuccessful) {
      $storageService.removeCookie({
        key: globalKeys.authKeys.passwordResetNumber,
      });
      $trackEvent('ForgotPassword', 'SUBMIT_RESET_CODE');
      navigateToSuccess();
    } else {
      errorCode.value = String(data?.error?.displayCode ?? 'general');
      errorRef.value = String(data?.error?.code ?? '');
    }
  } catch (error) {
    errorCode.value = String(error?.error?.displayCode ?? 'general');
    errorRef.value = String(error?.error?.code ?? '');
  } finally {
    formLoading.value = false;
  }
};
</script>
<template>
  <div class="p-4 enter-otp">
    <p class="font-bold text-base-priority pb-2">
      {{ $t('update-password') }}
    </p>
    <p class="text-xs text-dark-900 dark:text-light-50 py-1">
      {{ $t('enter-otp-and-new-password') }}
    </p>
    <div class="mb-0 pt-2 pb-4">
      <InputMask
        v-model="token"
        mask="9   9   9   9   9"
        placeholder="_ _ _ _ _"
        slot-char="_ _ _ _ _"
        autocomplete="one-time-code"
        class="w-full otp-token text-lg cursor-pointer"
        @update:model-value="verifyOTP()"
      />
    </div>
    <UserInterfaceDynamicFormBuilder
      ref="form"
      template-name="update password"
      :model-value="model"
      @loaded="formLoading = false"
    >
      <template v-if="!form?.isEmpty" #form-actions="formActionsProps">
        <div class="flex flex-col gap-3">
          <div
            v-if="
              formActionsProps?.form?.validated &&
              !formActionsProps?.form?.invalid
            "
          >
            <div class="flex items-center gap-2">
              <CheckCircleIcon
                class="dark:text-primary-blue-500 text-primary-blue-400 icon-success--border w-5"
              />
              <span class="text-xs font-light text-base-priority">
                {{ $t('Passwords-must-match-and-be-valid') }}
              </span>
            </div>
          </div>
        </div>
      </template>
    </UserInterfaceDynamicFormBuilder>
  </div>
  <div v-if="errorCode" class="mb-0 py-4 flex justify-center">
    <GenericError
      class="max-w-72 mx-auto"
      state="danger"
      :reference-code="errorRef"
    >
      {{ $t(`jpc-update-password-error-${errorCode}`) }}
    </GenericError>
  </div>

  <div class="p-4 bg-base text-sm flex gap-2 flex-row justify-center">
    <Button
      element-name="otp-back"
      class="capitalize w-full justify-center text-sm"
      group="secondary"
      type="secondary-alternative"
      rounding="lg"
      @click="navigateBack()"
    >
      {{ $t('back') }}
    </Button>
    <Button
      element-name="otp-verify"
      class="capitalize w-full justify-center text-sm"
      :disabled="!otpValid || !(form.validated && !form.invalid)"
      rounding="lg"
      @click="confirmOtp()"
    >
      {{ $t('verify') }}
    </Button>
  </div>
  <LazyUserInterfaceGenericLoader v-if="formLoading" container />
</template>
<style scoped>
.circle-icon {
  @apply mr-1;
}
</style>
<style>
.otp-token {
  @apply pl-5 tracking-[5px] text-lg text-center dark:placeholder:text-dark-600 placeholder:text-light-400;
}

@media (max-width: 420px) {
  .otp-token {
    @apply tracking-[3px];
  }
}
</style>
